import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  InputAdornment,
  Link,
  TextField,
  useTheme,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth.store';
import AuthPageShell from '../components/auth/AuthPageShell';
import AuthSuccessPanel from '../components/auth/AuthSuccessPanel';
import { authFieldSx, authSubmitButtonSx } from '../components/auth/authPage.styles';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [success, setSuccess] = useState(false);
  const { forgotPassword, isLoading } = useAuthStore();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await forgotPassword({
      username,
      platform: 'ESG',
    });
    setSuccess(true);
  };

  return (
    <AuthPageShell
      title={t('auth.forgotTitle')}
      subtitle={success ? undefined : t('auth.forgotSubtitle')}
    >
      {success ? (
        <AuthSuccessPanel
          icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 36 }} />}
          title={t('auth.resetLinkSentTitle')}
          message={t('auth.resetLinkSent')}
          actionLabel={t('auth.backToSignIn')}
        />
      ) : (
        <>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              required
              label={t('auth.email')}
              type="email"
              margin="normal"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{ ...authFieldSx, mt: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon
                      fontSize="small"
                      sx={{ color: 'text.secondary' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={authSubmitButtonSx(theme)}
            >
              {isLoading ? t('auth.sending') : t('auth.sendResetLink')}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2.5 }}>
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary' }}
            >
              {t('auth.backToSignIn')}
            </Link>
          </Box>
        </>
      )}
    </AuthPageShell>
  );
};

export default ForgotPassword;
